import {
  BellRing,
  Bug,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  Image,
  Mail,
  NotebookPen,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo, breadcrumbSchema } from '../lib/seo';

const supportTopics = [
  {
    icon: Bug,
    title: 'The app pauses or stops responding',
    steps: [
      'Close Focora completely and reopen it.',
      'Restart the device if the problem continues.',
      'Confirm that iOS or iPadOS and Focora are updated.',
      'Email support with the screen where it happened and the steps immediately before it.',
    ],
  },
  {
    icon: BellRing,
    title: 'Reminders or sounds do not play',
    steps: [
      'Open iOS or iPadOS Settings → Notifications → Focora and allow notifications and sounds.',
      'Check the device volume, silent mode, Focus mode and scheduled notification summary.',
      'Open Focora Settings and confirm reminders, sounds and vibration are enabled.',
      'Re-select the sound in Focora Settings and use the preview button to confirm the chosen sound.',
    ],
  },
  {
    icon: Clock3,
    title: 'A focus or habit timer looks incorrect',
    steps: [
      'Open the timer again so Focora can refresh it from the device clock.',
      'Avoid manually changing the device date or time while a timer is running.',
      'Only one Focora focus or habit timer can run at a time.',
      'Send support the timer duration, device model and what happened when the app was backgrounded.',
    ],
  },
  {
    icon: Image,
    title: 'Profile photo help',
    steps: [
      'Choose Add photo or Change photo from Focora Settings.',
      'Allow access to the selected photo when iOS asks.',
      'The resized profile image stays in Focora’s local app storage.',
      'Use Remove in Settings to delete the saved profile picture.',
    ],
  },
  {
    icon: NotebookPen,
    title: 'Habits, notes or progress disappeared',
    steps: [
      'Focora currently stores app information locally rather than in a cloud account.',
      'Deleting the app, clearing its storage or using Delete all data permanently removes local information.',
      'Use Export my data before deleting or moving away from the app if you want a readable personal copy.',
    ],
  },
  {
    icon: Download,
    title: 'Exporting your data',
    steps: [
      'Open Focora Settings → Your data → Export my data.',
      'Choose a destination from the iOS share sheet, such as Files, Mail or another app.',
      'The export is organized into profile, preferences, habits, focus sessions and notes.',
      'Focora does not automatically upload the export; you decide where it goes.',
    ],
  },
];

export function FocoraSupportPage() {
  return (
    <section className="page-section focora-public-page">
      <Seo
        title="Focora App Support"
        description="Official support for the Focora focus timer, habit tracker and notes app, with troubleshooting, privacy information and contact details."
        path="/focora/support"
        noindex
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Focora Support', path: '/focora/support' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Focora',
            applicationCategory: 'ProductivityApplication',
            operatingSystem: 'iOS, iPadOS',
            url: 'https://nexhanceai.com/focora/support',
            publisher: { '@type': 'Organization', name: 'Nexhance AI', url: 'https://nexhanceai.com/' },
          },
        ]}
      />

      <div className="shell focora-public-shell">
        <header className="focora-public-hero focora-support-hero">
          <div className="focora-app-mark" aria-hidden="true"><Smartphone size={30} /></div>
          <div className="focora-hero-copy">
            <p className="eyebrow">Official Focora support</p>
            <h1>How can we help?</h1>
            <p className="focora-hero-summary">
              Troubleshooting for focus timers, habits, reminders, notes, profile photos and local data.
            </p>
            <div className="focora-support-actions">
              <a className="button button-primary" href="mailto:info@nexhanceai.com?subject=Focora%20App%20Support">
                <Mail size={17} /> Email support
              </a>
              <Link className="button button-ghost" to="/focora/privacy">
                <ShieldCheck size={17} /> Privacy Policy
              </Link>
            </div>
          </div>
        </header>

        <div className="focora-support-status" role="note">
          <CheckCircle2 size={20} />
          <div>
            <strong>Support email: info@nexhanceai.com</strong>
            <span>Typical response target: one to two business days.</span>
          </div>
        </div>

        <section className="focora-support-section">
          <div className="focora-section-heading">
            <p className="eyebrow">Quick fixes</p>
            <h2>Find the issue you are seeing</h2>
            <p>These steps solve the most common device and permission problems without deleting your data.</p>
          </div>
          <div className="focora-support-grid">
            {supportTopics.map((topic) => {
              const Icon = topic.icon;
              return (
                <article key={topic.title} className="focora-support-card">
                  <div className="focora-support-icon"><Icon size={21} /></div>
                  <h3>{topic.title}</h3>
                  <ol>{topic.steps.map((step) => <li key={step}>{step}</li>)}</ol>
                </article>
              );
            })}
          </div>
        </section>

        <section className="focora-support-section focora-data-help">
          <div className="focora-section-heading">
            <p className="eyebrow">Your data</p>
            <h2>Local storage controls</h2>
          </div>
          <div className="focora-data-grid">
            <article>
              <FileText size={21} />
              <div><h3>Readable export</h3><p>Use Settings → Your data → Export my data to create a categorized report before deleting or reinstalling the app.</p></div>
            </article>
            <article>
              <RefreshCw size={21} />
              <div><h3>Run setup again</h3><p>Use Run setup again to update your personal choices without removing your existing habits, notes or progress.</p></div>
            </article>
            <article>
              <Trash2 size={21} />
              <div><h3>Delete all data</h3><p>This permanently removes Focora habits, notes, sessions, preferences and the local profile-picture copy from the device.</p></div>
            </article>
          </div>
        </section>

        <section className="focora-contact-panel focora-support-contact">
          <div>
            <p className="eyebrow">Still need help?</p>
            <h2>Send a useful support report</h2>
            <p>Include your iPhone or iPad model, iOS or iPadOS version, Focora version, steps to reproduce the issue and a screenshot when possible. Remove private note or habit content from screenshots first.</p>
          </div>
          <a className="button button-primary" href="mailto:info@nexhanceai.com?subject=Focora%20App%20Support&body=Device%20model%3A%0AiOS%20or%20iPadOS%20version%3A%0AFocora%20version%3A%0A%0AWhat%20happened%3A%0A%0ASteps%20to%20reproduce%3A%0A">
            <Mail size={17} /> Create support email
          </a>
        </section>

        <nav className="focora-link-list" aria-label="Focora support links">
          <Link to="/focora/privacy"><ShieldCheck size={18} /><span><strong>Focora Privacy Policy</strong><small>How local app data, photos and reminders are handled</small></span><ChevronRight size={18} /></Link>
          <a href="mailto:info@nexhanceai.com?subject=Focora%20Feedback"><Mail size={18} /><span><strong>Send feedback</strong><small>Suggestions, feature ideas and general comments</small></span><ExternalLink size={18} /></a>
        </nav>
      </div>
    </section>
  );
}
